import { useState } from "react";
import { StartOverview, type OverviewFact, type OverviewTopic, type OverviewLang } from "./StartOverview";
import { StartReader, type ReadingSlide } from "./StartReader";
import { useDownloadEstimate } from "../lib/useDownloadEstimate";

/**
 * Start screen for a course that has a reading intro: shows the course OVERVIEW
 * by default and opens the reading pane only when the learner clicks "Read
 * introduction" (its "Course overview" link returns here). Owns the
 * overview/reader toggle and the live download estimate so the hook lives on
 * its own fiber (App renders this conditionally on the intro beat).
 */
export interface StartHostProps {
  courseTitle: string;
  kicker?: string;
  situation: string;
  goal: string;
  youDo: string;
  facts: OverviewFact[];
  topics: OverviewTopic[];
  languages: OverviewLang[];
  currentLocale: string;
  onLocale: (code: string) => void;
  slides: ReadingSlide[];
  readMinutes: number;
  ready: boolean;
  loadedBytes: number;
  totalBytes: number;
  onStart: () => void;
}

export function StartHost(props: StartHostProps) {
  const [reading, setReading] = useState(false);
  const { seconds, bytesPerSec } = useDownloadEstimate(props.loadedBytes, props.totalBytes, props.ready);

  if (reading) {
    return (
      <StartReader
        courseTitle={props.courseTitle}
        kicker={props.kicker}
        readMinutes={props.readMinutes}
        slides={props.slides}
        ready={props.ready}
        loadedBytes={props.loadedBytes}
        totalBytes={props.totalBytes}
        estimateSeconds={seconds}
        onStart={props.onStart}
        onBack={() => setReading(false)}
      />
    );
  }
  return (
    <StartOverview
      courseTitle={props.courseTitle}
      kicker={props.kicker}
      situation={props.situation}
      goal={props.goal}
      youDo={props.youDo}
      facts={props.facts}
      topics={props.topics}
      languages={props.languages}
      currentLocale={props.currentLocale}
      onLocale={props.onLocale}
      ready={props.ready}
      loadedBytes={props.loadedBytes}
      totalBytes={props.totalBytes}
      bytesPerSec={bytesPerSec}
      estimateSeconds={seconds}
      onStart={props.onStart}
      hasIntro={props.slides.length > 0}
      readMinutes={props.readMinutes}
      onReadIntro={() => setReading(true)}
    />
  );
}
